import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Descriptions,
  Modal,
  Popconfirm,
  Spin,
  Tag,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";
import { formatRelativeDateTime } from "../../utils/relative-date";
import { getAuthMe } from "../../services/auth/auth.service";
import {
  AuthMeResponse,
  AuthMeSession,
} from "../../services/auth/auth.types";
import { useAuth } from "../../hooks/useAuth";
import styles from "./styles.module.scss";

const { Title } = Typography;

const EMPTY_VALUE = "—";

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "Administrador",
  TEACHER: "Profesor",
  STUDENT: "Estudiante",
  COORDINATOR: "Coordinador",
};

// The identity fields the modal renders; both AuthMeResponse and the
// Redux-hydrated UserEntity satisfy it structurally.
interface ProfileIdentity {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

// Deliberately tiny UA parsing: we only surface a friendly browser/OS pair,
// so a full parser dependency is not worth it. Order matters — Edge embeds
// "Chrome/" and Chrome embeds "Safari/" in their UA strings.
export const parseUserAgent = (
  userAgent: string | null | undefined
): { browser: string; os: string } => {
  if (!userAgent) {
    return { browser: EMPTY_VALUE, os: EMPTY_VALUE };
  }

  let browser = EMPTY_VALUE;
  if (/edg(e|a|ios)?\//i.test(userAgent)) browser = "Edge";
  else if (/firefox\//i.test(userAgent)) browser = "Firefox";
  else if (/chrome\/|crios\//i.test(userAgent)) browser = "Chrome";
  else if (/safari\//i.test(userAgent)) browser = "Safari";

  let os = EMPTY_VALUE;
  if (/windows/i.test(userAgent)) os = "Windows";
  else if (/mac os x|macintosh/i.test(userAgent)) os = "Mac";
  else if (/linux/i.test(userAgent)) os = "Linux";

  return { browser, os };
};

const formatSessionStart = (isoDate: string): string =>
  formatRelativeDateTime(isoDate, EMPTY_VALUE);

const getInitials = (user: ProfileIdentity): string =>
  `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase();

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({
  open,
  onClose,
}) => {
  const { user: storeUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [me, setMe] = useState<AuthMeResponse | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAuthMe()
      .then((data) => {
        if (!cancelled) setMe(data);
      })
      .catch(() => {
        // Fall back to the Redux-hydrated user from the auth slice; session
        // fields render as "—" when we have no /auth/me data.
        if (!cancelled) setMe(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
    }
    onClose();
    // useAuth's logout revokes the refresh token and cleans local state —
    // navigation is explicit here.
    navigate("/login");
  };

  const user: ProfileIdentity | null = me ?? storeUser ?? null;
  const session: AuthMeSession | null = me?.session ?? null;
  const { browser, os } = parseUserAgent(session?.user_agent);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Mi perfil"
      footer={
        <Popconfirm
          title="¿Cerrar sesión?"
          okText="Sí"
          cancelText="No"
          onConfirm={handleLogout}
        >
          <Button danger loading={loggingOut}>
            Cerrar sesión
          </Button>
        </Popconfirm>
      }
    >
      {loading ? (
        <div className={styles.spinContainer}>
          <Spin />
        </div>
      ) : user ? (
        <>
          <div className={styles.identity}>
            <Avatar size={72} className={styles.avatar}>
              {getInitials(user)}
            </Avatar>
            <Title level={4} className={styles.name}>
              {`${user.first_name} ${user.last_name}`}
            </Title>
            <Tag color="purple">{ROLE_LABELS[user.role] ?? user.role}</Tag>
          </div>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
            <Descriptions.Item label="Sesión iniciada">
              {session ? formatSessionStart(session.started_at) : EMPTY_VALUE}
            </Descriptions.Item>
            <Descriptions.Item label="Navegador">{browser}</Descriptions.Item>
            <Descriptions.Item label="Sistema">{os}</Descriptions.Item>
          </Descriptions>
        </>
      ) : (
        // getAuthMe failed and there is no Redux fallback user: say so
        // instead of rendering an empty modal. Logout stays available.
        <Alert
          type="warning"
          showIcon
          message="No se pudo cargar la información del perfil."
        />
      )}
    </Modal>
  );
};

export default ProfileModal;
